import * as d3 from 'd3';
import { uniq } from 'lodash';
import facebookIcon from './icons/facebook.svg';
import instagramIcon from './icons/instagram.svg';
import linkedinIcon from './icons/linkedin.svg';
import pinterestIcon from './icons/pinterest.svg';
import twitterIcon from './icons/twitter.svg';
import { getIntersections } from './get-intersection';

export class BubbleAnim {
    private elementRoot: HTMLElement | any;
    private rootSvg: any;
    private HEIGHT: number;
    private WIDTH: number;
    private iconGutterWidth = 5;
    private iconRadius = 100;
    private connections: any = {};
    private interval: any;

    private icons: any = {
        facebook: facebookIcon,
        instagram: instagramIcon,
        linkedin: linkedinIcon,
        pinterest: pinterestIcon,
        twitter: twitterIcon
    };

    constructor(containerId: string) {
        this.elementRoot = document.getElementById(containerId);
        this.HEIGHT = this.elementRoot.offsetHeight - 100;
        this.WIDTH = this.elementRoot.offsetWidth - 100;
        this.iconRadius = this.WIDTH * 0.1;

        const svg = d3.select(this.elementRoot)
            .append('svg')
            .attr('height', this.HEIGHT + 100)
            .attr('width', this.WIDTH + 100);

        this.rootSvg = svg.append('g')
            .attr('transform', 'translate(50, 50)')
            .attr('height', this.HEIGHT)
            .attr('width', this.WIDTH);

        this.addElements();

        window.onresize = () => {
            this.HEIGHT = this.elementRoot.offsetHeight - 100;
            this.WIDTH = this.elementRoot.offsetWidth - 100;
            this.iconRadius = this.WIDTH * 0.1;
            this.rootSvg.selectAll('*').remove();

            clearInterval(this.interval);
            this.addElements();
        };
    }

    public destroy() {
        clearInterval(this.interval);
    }

    public addElements() {
        this.addCenterCircle();
        this.addIcon('facebook', -0.4, -0.2);
        this.connectIconToCenter('facebook');
        this.addIcon('instagram', 0, -0.4);
        this.connectIcons('facebook', 'instagram');
        this.connectIconToCenter('instagram');
        this.addIcon('linkedin', 0.4, -0.3);
        this.connectIcons('instagram', 'linkedin');
        this.connectIconToCenter('linkedin');
        this.addIcon('pinterest', 0.3, 0.3);
        this.connectIcons('linkedin', 'pinterest');
        this.connectIconToCenter('pinterest');
        this.addIcon('twitter', -0.3, 0.3);
        this.connectIcons('pinterest', 'twitter');
        this.connectIcons('twitter', 'facebook');
        this.connectIconToCenter('twitter');

        this.animate();

        this.interval = setInterval(() => {
            this.animate();
        }, 10000);
    }

    public animate() {
        const self = this;
        const metrica = d3.scaleLinear().domain([0, 1]).range([-0.1, 0.1]);

        // Animate icons
        this.rootSvg.selectAll('.social-icon')
            .transition()
            .duration(10000)
            .ease(d3.easeLinear)
            .attrTween('transform', function () {
                const entrophyX = metrica(Math.random());
                const entrophyY = metrica(Math.random());
                const ref = d3.select(this);
                const originalTX: any = ref.attr('originalTX');
                const originalTY: any = ref.attr('originalTY');
                const iconType: any = ref.attr('iconType');

                return (t) => {
                    self.connections[iconType].forEach((t: any) => {
                        self.connectIconToCenter(iconType);
                        self.connectIcons(iconType, t);
                    });

                    if (t <= .5) {
                        return `translate(${originalTX * (1 + entrophyX * t)}, ${originalTY * (1 + entrophyY * t)})`;
                    } else {
                        return `translate(${originalTX * (1 + entrophyX * (1 - t))}, ${originalTY * (1 + entrophyY * (1 - t))})`;
                    }
                };
            });
    }

    private addIcon(type: string, xFromCenter: number, yFromCenter: number) {
        const data = this.icons[type];
        const height = this.iconRadius;
        const width = this.iconRadius;

        const posX = (this.WIDTH / 2) + (this.WIDTH * xFromCenter);
        const posY = ((this.HEIGHT / 2) + (this.HEIGHT * yFromCenter));

        const g = this.rootSvg.append('g')
            .attr('class', 'social-icon')
            .attr('id', 'social-icon-' + type)
            .attr('iconType', type)
            .attr('originalTX', posX)
            .attr('originalTY', posY)
            .attr('transform', `translate(${posX}, ${posY})`);

        g.append('circle')
            .attr('r', (height / 2) + this.iconGutterWidth)
            .attr('stroke', 'white')
            .attr('stroke-width', 1)
            .attr('opacity', 0.2)
            .attr('fill', 'transparent');

        g.append('svg:image')
            .attr('xlink:href', data)
            .attr('width', width)
            .attr('height', height)
            .attr('x', -(width / 2))
            .attr('y', -(height / 2))
            // ANIM
            .attr('opacity', 0)
            .transition()
            .duration(1000)
            .attr('opacity', 1);
    }

    private addCenterCircle() {
        const height = this.iconRadius * 2;

        const g = this.rootSvg.append('g')
            .attr('class', 'center-icon')
            .attr('id', 'center-icon')
            .attr('transform', `translate(${(this.WIDTH / 2)}, ${(this.HEIGHT / 2)})`);

        g.append('circle')
            .attr('r', (height / 2))
            .attr('stroke', 'white')
            .attr('stroke-width', .5)
            .attr('opacity', 0.2)
            .attr('fill', 'white')
            // ANIM
            .attr('opacity', 0)
            .transition()
            .duration(2000)
            .attr('opacity', 0.2);

        g.append('circle')
            .attr('r', (height / 2) + (this.iconGutterWidth * 3))
            .attr('stroke', 'white')
            .attr('stroke-width', .5)
            .attr('opacity', 0.2)
            .attr('fill', 'transparent');
    }

    private connectIconToCenter(i1: string) {
        d3.select(`#center-line-to-${i1}`).remove();

        const icon1 = this.rootSvg.select(`#social-icon-${i1}`);
        const icon1Position = this.getTranslation(icon1.attr('transform'));

        const centerIcon = this.rootSvg.select(`#center-icon`);
        const centerIconPosition = this.getTranslation(centerIcon.attr('transform'));

        const icon1CirclePoint: any = getIntersections([
            icon1Position.x,
            icon1Position.y
        ], [
            centerIconPosition.x,
            centerIconPosition.y
        ], [
            centerIconPosition.x,
            centerIconPosition.y,
            this.iconRadius + (this.iconGutterWidth * 3)
        ]);

        const icon2CirclePoint: any = getIntersections([
            centerIconPosition.x,
            centerIconPosition.y
        ], [
            icon1Position.x,
            icon1Position.y
        ], [
            icon1Position.x,
            icon1Position.y,
            (this.iconRadius / 2) + this.iconGutterWidth
        ]);

        this.rootSvg.append('line')
            .attr('id', `center-line-to-${i1}`)
            .attr('stroke', 'white')
            .attr('stroke-width', 1)
            .attr('opacity', 0.2)
            .attr('x1', icon2CirclePoint.points.intersection1.coords[0])
            .attr('x2', icon1CirclePoint.points.intersection1.coords[0])
            .attr('y1', icon2CirclePoint.points.intersection1.coords[1])
            .attr('y2', icon1CirclePoint.points.intersection1.coords[1]);
    }

    private connectIcons(i1: string, i2: string) {
        this.connections[i1] = this.connections[i1] || [];
        this.connections[i1].push(i2);
        this.connections[i1] = uniq(this.connections[i1]);

        this.rootSvg.select(`#connection-${i1}-${i2}`).remove();

        const icon1 = this.rootSvg.select(`#social-icon-${i1}`);
        const icon1Position = this.getTranslation(icon1.attr('transform'));
        const icon2 = this.rootSvg.select(`#social-icon-${i2}`);
        const icon2Position = this.getTranslation(icon2.attr('transform'));

        const icon1CirclePoint: any = getIntersections([
            icon1Position.x,
            icon1Position.y
        ], [
            icon2Position.x,
            icon2Position.y
        ], [
            icon2Position.x,
            icon2Position.y,
            (this.iconRadius / 2) + this.iconGutterWidth
        ]);

        const icon2CirclePoint: any = getIntersections([
            icon2Position.x,
            icon2Position.y
        ], [
            icon1Position.x,
            icon1Position.y
        ], [
            icon1Position.x,
            icon1Position.y,
            (this.iconRadius / 2) + this.iconGutterWidth
        ]);

        this.rootSvg.append('line')
            .attr('id', `connection-${i1}-${i2}`)
            .attr('stroke', 'white')
            .attr('stroke-width', 1)
            .attr('opacity', 0.2)
            .attr('x1', icon2CirclePoint.points.intersection1.coords[0])
            .attr('x2', icon1CirclePoint.points.intersection1.coords[0])
            .attr('y1', icon2CirclePoint.points.intersection1.coords[1])
            .attr('y2', icon1CirclePoint.points.intersection1.coords[1]);
    }

    private getTranslation(transform: any) {
        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttributeNS('', 'transform', transform);
        const matrix = g.transform.baseVal.consolidate()!.matrix;

        return {
            x: matrix.e,
            y: matrix.f,
            matrix: matrix
        };
    }
}
